import { useState, useEffect } from 'react'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import { TextStyle } from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import {
    Policy,
    PolicyType,
    PolicyStatus,
} from '@/lib/api/common/interfaces/policy.interface'
import {
    getPolicy,
    createPolicy,
    updatePolicy,
} from '@/lib/api/policies/policies-api'
import { BiDotsVertical, BiSave } from 'react-icons/bi'
import { BsEye } from 'react-icons/bs'
import {
    MdFormatBold,
    MdFormatItalic,
    MdFormatUnderlined,
    MdFormatStrikethrough,
    MdFormatListBulleted,
    MdFormatListNumbered,
    MdFormatAlignLeft,
    MdFormatAlignCenter,
    MdFormatAlignRight,
    MdFormatAlignJustify,
    MdLink,
    MdTableChart,
    MdRedo,
    MdUndo,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import ConfirmModal from '@/components/confirm-modal'

const policyTypeLabels: Record<PolicyType, string> = {
    TERMS_OF_SERVICE: 'Termos de Uso',
    PRIVACY_POLICY: 'Política de Privacidade',
    COOKIE_POLICY: 'Política de Cookies',
    LGPD_PRIVACY_NOTICE: 'Aviso de Privacidade LGPD',
    GDPR_POLICY: 'Política GDPR',
    CCPA_POLICY: 'Política CCPA',
    DATA_CONSENT_DECLARATION: 'Declaração de Consentimento',
    NDA: 'Acordo de Confidencialidade',
    SECURITY_POLICY: 'Política de Segurança',
    SERVICE_CONTRACT: 'Contrato de Serviço',
    CODE_OF_CONDUCT: 'Código de Conduta',
    DATA_RETENTION_DISPOSAL_POLICY: 'Política de Retenção de Dados',
    REFUND_POLICY: 'Política de Reembolso',
    RETURN_POLICY: 'Política de Devolução',
    SHIPPING_POLICY: 'Política de Envio',
    CANCELLATION_POLICY: 'Política de Cancelamento',
    PAYMENT_POLICY: 'Política de Pagamento',
    SUBSCRIPTION_POLICY: 'Política de Assinaturas',
    ACCEPTABLE_USE_POLICY: 'Política de Uso Aceitável',
    COMMUNITY_GUIDELINES: 'Diretrizes da Comunidade',
    DISCLAIMER: 'Aviso Legal',
    END_USER_LICENSE_AGREEMENT: 'EULA',
    COPYRIGHT_POLICY: 'Política de Direitos Autorais',
    TRADEMARK_POLICY: 'Política de Marcas',
    THIRD_PARTY_POLICY: 'Política de Terceiros',
    MARKETING_POLICY: 'Política de Marketing',
    EMPLOYEE_PRIVACY_POLICY: 'Privacidade de Funcionários',
    INTERNAL_USE_POLICY: 'Política de Uso Interno',
    OTHER: 'Outro',
}

export default function PolicyFormPage() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { id: policyId } = useParams<{ id: string }>()
    const policyType = searchParams.get('type') as PolicyType

    const [policy, setPolicy] = useState<Policy | null>(null)
    const [loading, setLoading] = useState(!!policyId)
    const [saving, setSaving] = useState(false)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [showPreviewModal, setShowPreviewModal] = useState(false)
    const [previewContent, setPreviewContent] = useState('')
    const [status, setStatus] = useState<PolicyStatus>(PolicyStatus.DRAFT)

    const editor = useEditor({
        extensions: [
            StarterKit,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Link.configure({ openOnClick: false }),
            TextStyle,
            Color,
            Highlight,
            Underline,
            Table.configure({ resizable: true }),
            TableRow,
            TableHeader,
            TableCell,
        ],
        content: '<p>Digite o conteúdo da política aqui...</p>',
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[400px] p-4',
            },
        },
        immediatelyRender: true,
    })

    const addLink = () => {
        if (!editor) return
        const url = window.prompt('URL do link:')
        if (url) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
        }
    }

    const insertTable = () => {
        if (!editor) return
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
    }

    const ToolbarButton = ({
        onClick,
        isActive = false,
        children,
        title,
    }: {
        onClick: () => void
        isActive?: boolean
        children: React.ReactNode
        title: string
    }) => (
        <button
            onClick={onClick}
            className={`btn btn-sm btn-ghost p-1 min-h-0 h-8 w-8 ${
                isActive ? 'bg-blue-100 text-blue-600' : ''
            }`}
            title={title}
        >
            {children}
        </button>
    )

    useEffect(() => {
        if (policyId) {
            loadPolicy()
        } else if (policyType) {
            setStatus(PolicyStatus.DRAFT)
        } else {
            navigate('/policies')
        }
    }, [policyId, policyType, navigate])

    useEffect(() => {
        if (editor && policy && policy.content) {
            editor.commands.setContent(policy.content)
        }
    }, [editor, policy])

    const loadPolicy = async () => {
        if (!policyId) return

        try {
            setLoading(true)
            const response = await getPolicy(Number(policyId))

            if (response.success) {
                setPolicy(response.result)
                setStatus(response.result.status)
            } else {
                toast.error(response.error || 'Erro ao carregar política')
                navigate('/policies')
            }
        } catch (error) {
            console.error('Erro ao carregar política:', error)
            toast.error('Erro ao carregar política')
            navigate('/policies')
        } finally {
            setLoading(false)
        }
    }

    const formatVersion = (version: number) => {
        const versionStr = version.toString()
        if (versionStr.length === 1) {
            return `0.${versionStr}`
        }
        const majorVersion = versionStr.slice(0, -1) || '0'
        const minorVersion = versionStr.slice(-1)
        return `${majorVersion}.${minorVersion}`
    }

    const formatDate = (dateString: string) => {
        return new Date(dateString)
            .toLocaleString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            })
            .replace(',', ' de')
    }

    const getStatusBadge = (s: PolicyStatus) => {
        const statusMap = {
            PUBLISHED: { label: 'Publicado', bg: '#DEF0DE', color: '#5CB85C' },
            DRAFT: { label: 'Rascunho', bg: '#FFF4E5', color: '#FF9800' },
            IN_ANALYSIS: { label: 'Revisão jurídica', bg: '#E3F0F9', color: '#2196F3' },
            INACTIVE: { label: 'Inativo', bg: 'rgba(245, 74, 0, 0.2)', color: '#F54A00' },
        }
        const statusInfo = statusMap[s] || { label: s, bg: '#F3F4F6', color: '#6B7280' }

        return (
            <span
                className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium"
                style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}
            >
                {statusInfo.label}
            </span>
        )
    }

    const handleSave = async () => {
        if (!editor) return

        const content = editor.getHTML()

        if (!content || content.trim() === '<p></p>') {
            toast.error('O conteúdo não pode estar vazio')
            return
        }

        try {
            setSaving(true)

            if (policy && policy.status !== PolicyStatus.PUBLISHED) {
                const response = await updatePolicy(policy.id, { content, status })

                if (response.success) {
                    toast.success('Política atualizada com sucesso')
                    setPolicy(response.result)
                } else {
                    toast.error(response.error || 'Erro ao atualizar política')
                }
            } else {
                const response = await createPolicy({
                    type: policy ? policy.type : policyType,
                    version: policy ? +policy.version + 10 : 10,
                    status: policy ? PolicyStatus.DRAFT : status,
                    content,
                })

                if (response.success) {
                    toast.success('Política salva com sucesso')
                    navigate(`/policies/${response.result.id}`)
                } else {
                    toast.error(response.error || 'Erro ao criar política')
                }
            }
        } catch (error) {
            console.error('Erro ao salvar política:', error)
            toast.error('Erro ao salvar política')
        } finally {
            setSaving(false)
            setShowConfirmModal(false)
        }
    }

    const getSubmitInfo = () => {
        if (!policy || policy.status !== PolicyStatus.PUBLISHED) {
            return 'Seu documento ainda não será publicado. Você poderá editá-lo a qualquer momento antes de publicar.'
        }
        return 'Atenção: Este documento já está publicado! As edições serão salvas como uma nova versão em rascunho e não ficarão visíveis para os usuários até que você clique em Publicar.'
    }

    const handlePreview = () => {
        if (!editor) return

        const content = editor.getHTML()
        const htmlContent = `
            <!DOCTYPE html>
            <html>
                <head>
                    <title>Preview - ${getCurrentTitle()}</title>
                    <meta charset="utf-8">
                    <meta name="viewport" content="width=device-width, initial-scale=1">
                    <style>
                        body {
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
                            max-width: 800px;
                            margin: 0 auto;
                            padding: 40px 20px;
                            line-height: 1.6;
                            color: #333;
                        }
                        h1, h2, h3, h4, h5, h6 { color: #2563eb; margin-top: 2em; margin-bottom: 0.5em; }
                        h1 { border-bottom: 2px solid #e5e7eb; padding-bottom: 0.5em; margin-bottom: 1em; }
                        p { margin-bottom: 1em; }
                        ul, ol { margin-bottom: 1em; padding-left: 2em; }
                        blockquote { border-left: 4px solid #e5e7eb; margin: 1em 0; padding: 0 1em; color: #6b7280; }
                    </style>
                </head>
                <body>
                    <p style="color: #6b7280; font-size: 0.9em; margin-bottom: 2em;">
                        Versão ${getCurrentVersion()} | ${getLastModified()}
                    </p>
                    ${content}
                </body>
            </html>
        `

        setPreviewContent(htmlContent)
        setShowPreviewModal(true)
    }

    const getCurrentTitle = () => {
        if (policy) return policyTypeLabels[policy.type] || policy.type
        return policyTypeLabels[policyType] || policyType
    }

    const getCurrentVersion = () => {
        if (policy) return formatVersion(policy.version)
        return '1.0'
    }

    const getLastModified = () => {
        if (policy) return formatDate(policy.updatedAt)
        return formatDate(new Date().toISOString())
    }

    if (loading) {
        return (
            <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Carregando política...</p>
            </div>
        )
    }

    if (!policyType && !policy) {
        return null
    }

    return (
        <div className="min-h-screen bg-[#F2F2F2]">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-bold">
                                    {getCurrentTitle()}
                                </h1>
                                <div className="flex items-center gap-3 ml-3">
                                    {getStatusBadge(status)}
                                    {policy && (
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-secondary"
                                            checked={status === PolicyStatus.PUBLISHED}
                                            onChange={e =>
                                                setStatus(
                                                    e.target.checked
                                                        ? PolicyStatus.PUBLISHED
                                                        : PolicyStatus.DRAFT,
                                                )
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                                Versão {getCurrentVersion()} | última modificação {getLastModified()}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setShowConfirmModal(true)}
                            disabled={saving}
                            className="btn btn-ghost"
                        >
                            <BiSave className="text-2xl" />{' '}
                            {saving ? 'Salvando...' : 'Salvar'}
                        </button>

                        <button onClick={handlePreview} className="btn btn-ghost">
                            <BsEye className="text-2xl" /> Prévia
                        </button>

                        <div className="dropdown dropdown-end">
                            <button tabIndex={0} className="btn btn-ghost p-0 min-h-0 w-8">
                                <BiDotsVertical className="text-base" />
                            </button>
                            <ul
                                tabIndex={0}
                                className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-52 z-20"
                            >
                                <li>
                                    <a onClick={() => navigate('/policies')}>
                                        Voltar para lista
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Editor */}
            <div className="p-6" style={{ backgroundColor: '#F2F2F2' }}>
                <div className="w-full mx-auto">
                    <div className="bg-white rounded-lg w-full shadow-sm border border-gray-200 min-h-[500px]">
                        {editor && (
                            <div className="border-b border-gray-200 p-3 flex flex-wrap gap-1 bg-white rounded-t-lg items-center">
                                <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Desfazer">
                                    <MdUndo className="text-base" />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Refazer">
                                    <MdRedo className="text-base" />
                                </ToolbarButton>

                                <div className="border-l border-gray-300 mx-1 h-6"></div>

                                <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Negrito">
                                    <MdFormatBold className="text-base" />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Itálico">
                                    <MdFormatItalic className="text-base" />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Sublinhado">
                                    <MdFormatUnderlined className="text-base" />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Riscado">
                                    <MdFormatStrikethrough className="text-base" />
                                </ToolbarButton>

                                <div className="border-l border-gray-300 mx-1 h-6"></div>

                                <select
                                    className="select select-sm w-20 text-xs"
                                    value={
                                        editor.isActive('heading', { level: 1 })
                                            ? 'h1'
                                            : editor.isActive('heading', { level: 2 })
                                            ? 'h2'
                                            : editor.isActive('heading', { level: 3 })
                                            ? 'h3'
                                            : 'p'
                                    }
                                    onChange={e => {
                                        const value = e.target.value
                                        if (value === 'p') {
                                            editor.chain().focus().setParagraph().run()
                                        } else {
                                            const level = parseInt(value.replace('h', ''))
                                            editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run()
                                        }
                                    }}
                                >
                                    <option value="p">P</option>
                                    <option value="h1">H1</option>
                                    <option value="h2">H2</option>
                                    <option value="h3">H3</option>
                                </select>

                                <div className="border-l border-gray-300 mx-1 h-6"></div>

                                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Alinhar à esquerda">
                                    <MdFormatAlignLeft className="text-base" />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Centralizar">
                                    <MdFormatAlignCenter className="text-base" />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Alinhar à direita">
                                    <MdFormatAlignRight className="text-base" />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('justify').run()} isActive={editor.isActive({ textAlign: 'justify' })} title="Justificar">
                                    <MdFormatAlignJustify className="text-base" />
                                </ToolbarButton>

                                <div className="border-l border-gray-300 mx-1 h-6"></div>

                                <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Lista com marcadores">
                                    <MdFormatListBulleted className="text-base" />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Lista numerada">
                                    <MdFormatListNumbered className="text-base" />
                                </ToolbarButton>

                                <div className="border-l border-gray-300 mx-1 h-6"></div>

                                <ToolbarButton onClick={addLink} isActive={editor.isActive('link')} title="Adicionar link">
                                    <MdLink className="text-base" />
                                </ToolbarButton>
                                <ToolbarButton onClick={insertTable} title="Inserir tabela">
                                    <MdTableChart className="text-base" />
                                </ToolbarButton>
                            </div>
                        )}

                        <EditorContent editor={editor} />
                    </div>
                </div>
            </div>

            {/* Modal de Preview */}
            <div className={`modal ${showPreviewModal ? 'modal-open' : ''}`}>
                <div className="modal-box w-11/12 max-w-5xl h-[85vh] p-0 flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
                        <h3 className="text-lg font-bold">
                            Preview - {getCurrentTitle()}
                        </h3>
                        <button
                            className="btn btn-sm btn-circle btn-ghost"
                            onClick={() => setShowPreviewModal(false)}
                        >
                            X
                        </button>
                    </div>
                    <div className="flex-1 overflow-hidden">
                        <iframe
                            srcDoc={previewContent}
                            className="w-full h-full border-0"
                            title="Preview da Política"
                            sandbox="allow-same-origin"
                        />
                    </div>
                </div>
                <div
                    className="modal-backdrop"
                    onClick={() => setShowPreviewModal(false)}
                />
            </div>

            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleSave}
                title="Salvar rascunho?"
                message={getSubmitInfo()}
                confirmButtonText="Salvar rascunho"
                loading={saving}
                confirmButtonClass="btn btn-secondary"
            />
        </div>
    )
}
